import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { exportTransferSummary, listFundRequests } from '../../services/fundRequestService';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const isAccountingRole = (roleName) => (roleName || '').toLowerCase() === 'accounting';

const getEffectiveStatus = (item) => {
  const status = (item.status || '').toLowerCase();
  if (status !== 'in_review') return status;
  const hasPendingNonAccounting = (item.approvalSteps || []).some(
    s => s.status === 'pending' && !isAccountingRole(s.role?.name || s.roleName)
  );
  return hasPendingNonAccounting ? status : 'approved';
};

const TransferSummary = () => {
  const today = new Date().toISOString().slice(0, 10);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listFundRequests({ limit: 500, page: 1 });
      const rows = res.rows || res.data || res.fundRequests || [];
      setData(rows);
    } catch (err) {
      console.error('Failed to fetch fund requests for summary', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDownload = async () => {
    if (!selectedDate) {
      alert('Pilih tanggal transfer terlebih dahulu');
      return;
    }
    try {
      const blob = await exportTransferSummary(selectedDate);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transfer-summary-${selectedDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download summary', err);
      alert(err.response?.data?.message || err.message || 'Gagal download summary');
    }
  };

  const filtered = useMemo(() => {
    if (!selectedDate) return [];
    const allowedStatuses = ['approved', 'released'];
    return data.filter((item) => {
      const effective = getEffectiveStatus(item);
      if (!allowedStatuses.includes(effective)) return false;
      const d = item.plannedTransferDate || item.submissionDate || item.createdAt;
      return d && d.slice(0, 10) === selectedDate;
    });
  }, [data, selectedDate]);

  const grandTotal = filtered.reduce((sum, it) => sum + Number(it.totalAmount || 0), 0);

  return (
    <Box>
      <Typography variant="h5" mb={2}>RINGKASAN TRANSFER HARIAN</Typography>
      <Card sx={{ mb: 2 }}>
        <CardHeader title="FILTER" />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="TANGGAL TRANSFER"
                InputLabelProps={{ shrink: true }}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>
                  MUAT ULANG
                </Button>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={loading}>
                  UNDUH RINGKASAN
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title={`TANGGAL TRANSFER: ${selectedDate || '-'}`} />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>NO.</TableCell>
                  <TableCell>PEMOHON</TableCell>
                  <TableCell>DESKRIPSI</TableCell>
                  <TableCell>BANK</TableCell>
                  <TableCell>NOMOR REKENING</TableCell>
                  <TableCell>PEMILIK REKENING</TableCell>
                  <TableCell align="right">TOTAL (RP)</TableCell>
                  <TableCell>PROYEK</TableCell>
                  <TableCell>STATUS</TableCell>
                  <TableCell align="center">AKSI</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, idx) => (
                  <TableRow key={row.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{row.requester?.fullName || row.requester?.username || '-'}</TableCell>
                    <TableCell>{row.title || row.description || row.category || '-'}</TableCell>
                    <TableCell>{row.bankName || '-'}</TableCell>
                    <TableCell>{row.accountNumber || '-'}</TableCell>
                    <TableCell>{row.accountHolder || '-'}</TableCell>
                    <TableCell align="right">{Number(row.totalAmount || 0).toLocaleString('id-ID')}</TableCell>
                    <TableCell>{row.project?.name || 'HO / NON PROYEK'}</TableCell>
                    <TableCell>{row.status === 'released' ? 'SUDAH TRANSFER' : 'SIAP TRANSFER'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" onClick={() => navigate(`/fund-requests/${row.id}`)}>
                        DETAIL
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {loading ? 'MEMUAT...' : 'TIDAK ADA DATA'}
                    </TableCell>
                  </TableRow>
                )}
                {filtered.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={6}><strong>TOTAL KESELURUHAN</strong></TableCell>
                    <TableCell align="right"><strong>{grandTotal.toLocaleString('id-ID')}</strong></TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

        </CardContent>
      </Card>
    </Box>
  );
};

export default TransferSummary;
